import axios from "axios";
import type { AxiosInstance, InternalAxiosRequestConfig } from "axios";
import { msalInstance, apiRequest, msalConfig } from "../auth/authConfig";

// Log the configuration for debugging
console.log("API Configuration:", {
  clientId: msalConfig.auth.clientId,
  authority: msalConfig.auth.authority,
  scopes: apiRequest.scopes,
});

/**
 * Base API client for making authenticated requests to the LMS API
 */
const api: AxiosInstance = axios.create({
  baseURL: "/api/v1", // Use relative URL to leverage Azure Static Web App proxy
  timeout: 30000, // Increase timeout to 30 seconds
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: false, // Don't include credentials for CORS requests
});

// Log when the API client is created
console.log("API client created with baseURL:", api.defaults.baseURL);

// Add response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Log detailed error information
    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      console.error("API Error Response:", {
        status: error.response.status,
        headers: error.response.headers,
        data: error.response.data,
      });
    } else if (error.request) {
      // The request was made but no response was received
      console.error("API Error Request:", error.request);
    } else {
      // Something happened in setting up the request that triggered an Error
      console.error("API Error:", error.message);
    }
    
    return Promise.reject(error);
  }
);

// Request interceptor for adding auth token
api.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    try {
      // Get active account
      const account = msalInstance.getActiveAccount();
      if (!account) {
        console.error("No active account! Verify a user has been signed in and setActiveAccount has been called.");
        
        // Try to get all accounts and set the first one as active
        const accounts = msalInstance.getAllAccounts();
        if (accounts.length > 0) {
          console.log("Found accounts in cache, setting the first one as active");
          msalInstance.setActiveAccount(accounts[0]);
          // Continue with the first account
          const firstAccount = accounts[0];
          
          // Get access token with the first account
          const response = await msalInstance.acquireTokenSilent({
            ...apiRequest,
            account: firstAccount,
          }).catch(async (error) => {
            console.error("Silent token acquisition failed, acquiring token using redirect", error);
            // Redirect to login page
            msalInstance.loginRedirect(apiRequest);
            return Promise.reject(new Error("Redirecting to login..."));
          });
          
          // Add token to request headers
          config.headers.Authorization = `Bearer ${response.accessToken}`;
          return config;
        }
        
        // If no accounts are found, redirect to login
        console.log("No accounts found, redirecting to login");
        msalInstance.loginRedirect(apiRequest);
        return Promise.reject(new Error("Redirecting to login..."));
      }

      // Get access token
      const response = await msalInstance.acquireTokenSilent({
        ...apiRequest,
        account: account,
      }).catch(async (error) => {
        console.error("Silent token acquisition failed, acquiring token using popup", error);
        return await msalInstance.acquireTokenPopup({
          ...apiRequest,
          account: account,
        });
      });

      // Add token to request headers
      config.headers.Authorization = `Bearer ${response.accessToken}`;
      
      // Log the token for debugging (only the first few characters)
      console.log("Token acquired successfully. First 10 chars:", response.accessToken.substring(0, 10) + "...");
      
      return config;
    } catch (error) {
      console.error("Error acquiring token:", error);
      return Promise.reject(error);
    }
  },
  (error) => {
    return Promise.reject(error);
  }
);

export default api;
